/**
 * RACKET QUIZ — SCORING ENGINE
 * Pure scoring algorithm with zero DOM dependencies.
 * Scores each racket product against quiz answers using weighted multi-factor matching.
 */

const QuizScoring = (() => {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────

  const LEVEL_ORDER = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];

  const STYLE_MATRIX = {
    // [productStyle][userStyle] → multiplier (0–1)
    Power:   { power: 1.0, control: 0.15, hybrid: 0.6 },
    Control: { control: 1.0, power: 0.15, hybrid: 0.6 },
    Hybrid:  { power: 0.6, control: 0.6, hybrid: 1.0 }
  };

  const SHAPE_MATRIX = {
    // [userImprovement][productShape] → multiplier (0–1)
    more_power:   { Diamond: 1.0, Teardrop: 0.5, Hybrid: 0.45, Round: 0.2 },
    sweet_spot:   { Round: 1.0, Teardrop: 0.6, Hybrid: 0.55, Diamond: 0.2 },
    more_control: { Round: 1.0, Hybrid: 0.6, Teardrop: 0.5, Diamond: 0.15 },
    balance:      { Teardrop: 1.0, Hybrid: 1.0, Round: 0.6, Diamond: 0.5 }
  };

  const BUDGET_RANGES = {
    budget:  { min: 0, max: 800000 },       // 0–8,000 EGP (price in cents)
    mid:     { min: 800000, max: 1500000 },  // 8,000–15,000
    premium: { min: 1500000, max: 2300000 }, // 15,000–23,000
    elite:   { min: 2300000, max: Infinity }  // 23,000+
  };

  const BUDGET_ORDER = ['budget', 'mid', 'premium', 'elite'];

  const LEVEL_TYPICAL_BUDGET = {
    Beginner: 'budget',
    Intermediate: 'mid',
    Advanced: 'premium',
    Professional: 'elite'
  };

  const WEIGHTS = {
    style: 22,
    level: 17,
    shape: 13,
    budget: 10,
    position: 8,
    weakness: 8,
    physique: 7,
    comfort: 7,
    bonus: 8
  };

  // ── Factor Scoring Functions ───────────────────────────────────────────────

  function scoreStyle(productStyle, userStyle) {
    if (!productStyle || !userStyle) return WEIGHTS.style * 0.5;
    const row = STYLE_MATRIX[productStyle];
    if (!row) return WEIGHTS.style * 0.5;
    const multiplier = row[userStyle] ?? 0.3;
    return WEIGHTS.style * multiplier;
  }

  function scoreLevel(productLevel, userLevel) {
    if (!productLevel || !userLevel) return WEIGHTS.level * 0.5;
    const pIdx = LEVEL_ORDER.indexOf(productLevel);
    const uIdx = LEVEL_ORDER.indexOf(userLevel);
    if (pIdx === -1 || uIdx === -1) return WEIGHTS.level * 0.5;

    const distance = Math.abs(pIdx - uIdx);
    const distMultipliers = [1.0, 0.6, 0.3, 0.1];
    let multiplier = distMultipliers[distance] ?? 0.1;

    // Small penalty for product being higher level than user (harder to use)
    if (pIdx > uIdx && distance > 0) {
      multiplier *= 0.9;
    }

    return WEIGHTS.level * multiplier;
  }

  function scoreShape(productShape, userImprovement) {
    if (!productShape || !userImprovement) return WEIGHTS.shape * 0.5;
    const row = SHAPE_MATRIX[userImprovement];
    if (!row) return WEIGHTS.shape * 0.5;
    const multiplier = row[productShape] ?? 0.3;
    return WEIGHTS.shape * multiplier;
  }

  function scoreBudget(productPrice, userBudget) {
    if (!productPrice || !userBudget) return WEIGHTS.budget * 0.5;
    const range = BUDGET_RANGES[userBudget];
    if (!range) return WEIGHTS.budget * 0.5;

    // Proximity-based scoring within the user's tier
    // Products closer to the center of the range score higher
    var effectiveMax = range.max === Infinity ? range.min * 2 : range.max;
    var tierCenter = (range.min + effectiveMax) / 2;
    var tierSpan = effectiveMax - range.min;

    if (productPrice >= range.min && productPrice <= effectiveMax) {
      // Within range — score by proximity to center (0.7–1.0)
      var distFromCenter = Math.abs(productPrice - tierCenter);
      var proximityRatio = 1 - (distFromCenter / (tierSpan / 2));
      return WEIGHTS.budget * (0.7 + proximityRatio * 0.3);
    }

    // Outside range — score by distance from nearest edge
    var budgetIdx = BUDGET_ORDER.indexOf(userBudget);
    var nearestEdge = productPrice < range.min ? range.min : effectiveMax;
    var distFromRange = Math.abs(productPrice - nearestEdge);
    var rangeSize = tierSpan || 800000; // fallback for single-point ranges

    // Adjacent tier → 0.3–0.5 based on how close to the boundary
    if (distFromRange <= rangeSize) {
      var adjRatio = 1 - (distFromRange / rangeSize);
      return WEIGHTS.budget * (0.3 + adjRatio * 0.2);
    }

    // Far away → 0.05–0.3 based on relative distance
    var farRatio = Math.max(0, 1 - (distFromRange / (rangeSize * 3)));
    return WEIGHTS.budget * (0.05 + farRatio * 0.25);
  }

  function scoreCrossFactors(product, answers) {
    let bonus = 0;

    const pLevel = product.level || '';
    const pStyle = product.style || '';
    const pShape = product.shape || '';
    const pYear = parseInt(product.year) || 0;
    const currentYear = new Date().getFullYear();

    // Rule 1: Beginner safety net — beginner wanting power still gets beginner-appropriate rackets
    if (answers.level === 'Beginner' && answers.style === 'power') {
      if (pLevel === 'Beginner' && pStyle === 'Power') bonus += 3;
      if (pLevel === 'Advanced' || pLevel === 'Professional') bonus -= 2;
    }

    // Rule 2: Pro control coherence — pro players wanting control benefit from round pro rackets
    if (answers.level === 'Professional' && answers.style === 'control') {
      if (pShape === 'Round' && pLevel === 'Professional') bonus += 2;
    }

    // Rule 3: Budget-level alignment — reward affordable rackets at user's level
    if (answers.level && answers.budget) {
      const typicalBudget = LEVEL_TYPICAL_BUDGET[answers.level];
      const userBudgetIdx = BUDGET_ORDER.indexOf(answers.budget);
      const typicalIdx = BUDGET_ORDER.indexOf(typicalBudget);
      if (userBudgetIdx < typicalIdx) {
        // User budget is lower than typical for their level
        const range = BUDGET_RANGES[answers.budget];
        if (pLevel === answers.level && product.price <= range.max * 1.15) {
          bonus += 3;
        }
      }
    }

    // Rule 4: Freshness — newer models get a small boost
    if (pYear >= currentYear) {
      bonus += 2;
    } else if (pYear >= currentYear - 1) {
      bonus += 1;
    }

    return Math.max(0, Math.min(WEIGHTS.bonus, bonus));
  }

  // ── Position Scoring ──────────────────────────────────────────────────────
  // Court position + dominant hand determines if player needs more power or control.
  // "Natural" side (forehand toward center) → control benefits them.
  // "Cross" side (forehand toward glass) → power benefits them.

  const POSITION_NEED = {
    // right-handed on right side: forehand faces center → control player
    right_right: 'control',
    // right-handed on left side: forehand faces glass → needs power to finish
    right_left: 'power',
    // left-handed on left side: forehand faces center → control player
    left_left: 'control',
    // left-handed on right side: forehand faces glass → needs power
    left_right: 'power'
  };

  function scorePosition(productStyle, productShape, userPosition, product) {
    if (!userPosition) return WEIGHTS.position * 0.5;
    const need = POSITION_NEED[userPosition];
    if (!need) return WEIGHTS.position * 0.5;

    // Position scoring emphasizes physical racket characteristics (balance, weight)
    // over style (which is already scored separately in scoreStyle).
    // Blend: 30% style-based, 30% shape-based, 40% metadata-based

    var styleMult = 0.5;
    var shapeMult = 0.5;
    var metaMult = 0.5;

    if (need === 'power') {
      // Cross-side → benefits from head_heavy balance, heavier rackets, diamond/teardrop shapes
      styleMult = productStyle === 'Power' ? 0.9 : (productStyle === 'Hybrid' ? 0.6 : 0.3);
      shapeMult = productShape === 'Diamond' ? 1.0 : (productShape === 'Teardrop' ? 0.7 : (productShape === 'Hybrid' ? 0.5 : 0.2));

      // Metadata: balance_point and weight_class are the primary differentiators
      if (product) {
        var bpScore = product.balance_point === 'head_heavy' ? 1.0 : (product.balance_point === 'even' ? 0.5 : 0.2);
        var wcScore = product.weight_class === 'heavy' ? 1.0 : (product.weight_class === 'medium' ? 0.7 : 0.3);
        metaMult = bpScore * 0.6 + wcScore * 0.4;
      }
    } else {
      // Natural-side → benefits from head_light/even balance, lighter rackets, round shapes
      styleMult = productStyle === 'Control' ? 0.9 : (productStyle === 'Hybrid' ? 0.6 : 0.3);
      shapeMult = productShape === 'Round' ? 1.0 : (productShape === 'Hybrid' ? 0.6 : (productShape === 'Teardrop' ? 0.5 : 0.2));

      // Metadata: balance_point and weight_class for maneuverability
      if (product) {
        var bpScore = product.balance_point === 'head_light' ? 1.0 : (product.balance_point === 'even' ? 0.7 : 0.3);
        var wcScore = product.weight_class === 'light' ? 1.0 : (product.weight_class === 'medium' ? 0.7 : 0.3);
        metaMult = bpScore * 0.6 + wcScore * 0.4;
      }
    }

    // Blend: 25% style, 30% shape, 45% metadata
    // This reduces duplication with scoreStyle while emphasizing
    // the unique physical characteristics relevant to court position
    var combined = styleMult * 0.25 + shapeMult * 0.30 + metaMult * 0.45;
    return WEIGHTS.position * combined;
  }

  // ── Weakness Scoring ──────────────────────────────────────────────────────
  // Player's biggest struggle maps to ideal racket characteristics.
  // Emphasizes metadata (forgiveness, balance_point, weight_class) over style
  // to reduce duplication with the style factor.

  const WEAKNESS_META_IDEAL = {
    // glass_too_much: needs forgiveness + even/head_light balance + lighter weight
    glass_too_much: {
      forgiveness: { high: 1.0, medium: 0.6, low: 0.15 },
      balance_point: { head_light: 1.0, even: 0.8, head_heavy: 0.25 },
      weight_class: { light: 1.0, medium: 0.7, heavy: 0.3 },
      shapes: { Round: 1.0, Hybrid: 0.7, Teardrop: 0.45, Diamond: 0.15 }
    },
    // weak_smash: needs head_heavy + heavier + low forgiveness (stiff/powerful)
    weak_smash: {
      forgiveness: { low: 1.0, medium: 0.6, high: 0.2 },
      balance_point: { head_heavy: 1.0, even: 0.5, head_light: 0.15 },
      weight_class: { heavy: 1.0, medium: 0.7, light: 0.25 },
      shapes: { Diamond: 1.0, Teardrop: 0.65, Hybrid: 0.45, Round: 0.15 }
    },
    // mishits: needs high forgiveness + big sweet spot (round) + even balance
    mishits: {
      forgiveness: { high: 1.0, medium: 0.55, low: 0.1 },
      balance_point: { even: 1.0, head_light: 0.7, head_heavy: 0.3 },
      weight_class: { medium: 1.0, light: 0.8, heavy: 0.4 },
      shapes: { Round: 1.0, Hybrid: 0.65, Teardrop: 0.35, Diamond: 0.1 }
    },
    // defense_weak: needs forgiveness + head_light/even + lighter for quick reactions
    defense_weak: {
      forgiveness: { high: 1.0, medium: 0.6, low: 0.15 },
      balance_point: { head_light: 1.0, even: 0.8, head_heavy: 0.2 },
      weight_class: { light: 1.0, medium: 0.65, heavy: 0.2 },
      shapes: { Round: 1.0, Hybrid: 0.7, Teardrop: 0.4, Diamond: 0.1 }
    }
  };

  function scoreWeakness(productStyle, productShape, userWeakness, product) {
    if (!userWeakness) return WEIGHTS.weakness * 0.5;
    var ideal = WEAKNESS_META_IDEAL[userWeakness];
    if (!ideal) return WEIGHTS.weakness * 0.5;

    // Shape score
    var shapeMult = (ideal.shapes || {})[productShape] ?? 0.3;

    // Metadata scores (forgiveness, balance_point, weight_class)
    var fgMult = 0.5;
    var bpMult = 0.5;
    var wcMult = 0.5;

    if (product) {
      if (product.forgiveness && ideal.forgiveness) {
        fgMult = ideal.forgiveness[product.forgiveness] ?? 0.4;
      }
      if (product.balance_point && ideal.balance_point) {
        bpMult = ideal.balance_point[product.balance_point] ?? 0.4;
      }
      if (product.weight_class && ideal.weight_class) {
        wcMult = ideal.weight_class[product.weight_class] ?? 0.4;
      }
    }

    // Blend: 30% shape, 35% forgiveness, 20% balance_point, 15% weight_class
    // No style component — style is already scored in scoreStyle
    var combined = shapeMult * 0.30 + fgMult * 0.35 + bpMult * 0.20 + wcMult * 0.15;
    return WEIGHTS.weakness * combined;
  }

  // ── Physique Scoring ──────────────────────────────────────────────────────
  // Player's physical build determines ideal weight and balance of the racket.
  // Athletic → can handle heavier, head_heavy rackets.
  // Average → medium weight ideal.
  // Slim → lighter rackets, head_light/even balance preferred.

  function scorePhysique(product, userPhysique) {
    if (!userPhysique) return WEIGHTS.physique * 0.5;

    var weightClass = product.weight_class || '';
    var balancePoint = product.balance_point || '';
    var multiplier = 0.5;

    if (userPhysique === 'athletic') {
      // Athletic → heavier + head_heavy = great; light = less ideal
      var wMult = { heavy: 1.0, medium: 0.8, light: 0.4 };
      var bMult = { head_heavy: 1.0, even: 0.7, head_light: 0.45 };
      var w = wMult[weightClass] ?? 0.6;
      var b = bMult[balancePoint] ?? 0.6;
      multiplier = w * 0.55 + b * 0.45;
    } else if (userPhysique === 'average') {
      // Average → medium is ideal; extremes less ideal
      var wMult = { medium: 1.0, light: 0.65, heavy: 0.55 };
      var bMult = { even: 1.0, head_heavy: 0.6, head_light: 0.6 };
      var w = wMult[weightClass] ?? 0.6;
      var b = bMult[balancePoint] ?? 0.6;
      multiplier = w * 0.55 + b * 0.45;
    } else if (userPhysique === 'slim') {
      // Slim → lighter + head_light/even = ideal
      var wMult = { light: 1.0, medium: 0.65, heavy: 0.2 };
      var bMult = { head_light: 1.0, even: 0.8, head_heavy: 0.3 };
      var w = wMult[weightClass] ?? 0.6;
      var b = bMult[balancePoint] ?? 0.6;
      multiplier = w * 0.55 + b * 0.45;
    }

    return WEIGHTS.physique * multiplier;
  }

  // ── Comfort Scoring ───────────────────────────────────────────────────────
  // Player's comfort/injury status determines ideal forgiveness, weight, and balance.
  // no_issues → performance first, low forgiveness OK
  // slight_discomfort → medium forgiveness + lighter preferred
  // arm_issues → high forgiveness + light + even balance

  function scoreComfort(product, userComfort) {
    if (!userComfort) return WEIGHTS.comfort * 0.5;

    var forgiveness = product.forgiveness || '';
    var weightClass = product.weight_class || '';
    var balancePoint = product.balance_point || '';
    var multiplier = 0.5;

    if (userComfort === 'no_issues') {
      // No issues → performance first; low forgiveness OK (stiffer = more power/control)
      var fMult = { low: 0.9, medium: 1.0, high: 0.7 };
      var f = fMult[forgiveness] ?? 0.7;
      // Weight/balance don't matter much
      multiplier = f;
    } else if (userComfort === 'slight_discomfort') {
      // Sometimes uncomfortable → medium/high forgiveness + lighter weight
      var fMult = { high: 1.0, medium: 0.85, low: 0.3 };
      var wMult = { light: 1.0, medium: 0.8, heavy: 0.35 };
      var f = fMult[forgiveness] ?? 0.6;
      var w = wMult[weightClass] ?? 0.6;
      multiplier = f * 0.6 + w * 0.4;
    } else if (userComfort === 'arm_issues') {
      // Arm/elbow problems → high forgiveness mandatory + light + even/head_light balance
      var fMult = { high: 1.0, medium: 0.45, low: 0.1 };
      var wMult = { light: 1.0, medium: 0.5, heavy: 0.1 };
      var bMult = { even: 1.0, head_light: 0.9, head_heavy: 0.2 };
      var f = fMult[forgiveness] ?? 0.4;
      var w = wMult[weightClass] ?? 0.5;
      var b = bMult[balancePoint] ?? 0.5;
      multiplier = f * 0.5 + w * 0.3 + b * 0.2;
    }

    return WEIGHTS.comfort * multiplier;
  }

  // ── Main Scoring ───────────────────────────────────────────────────────────

  function scoreProduct(product, answers) {
    const breakdown = {
      style: scoreStyle(product.style, answers.style),
      level: scoreLevel(product.level, answers.level),
      shape: scoreShape(product.shape, answers.improvement),
      budget: scoreBudget(product.price, answers.budget),
      position: scorePosition(product.style, product.shape, answers.position, product),
      weakness: scoreWeakness(product.style, product.shape, answers.weakness, product),
      physique: scorePhysique(product, answers.physique),
      comfort: scoreComfort(product, answers.comfort),
      bonus: scoreCrossFactors(product, answers)
    };

    const rawScore = breakdown.style + breakdown.level + breakdown.shape + breakdown.budget + breakdown.position + breakdown.weakness + breakdown.physique + breakdown.comfort + breakdown.bonus;

    return {
      score: rawScore,
      breakdown,
      product
    };
  }

  function rankProducts(products, answers, limit) {
    limit = limit || 6;

    // Filter out unavailable products
    const available = products.filter(function (p) { return p.available !== false; });
    const pool = available.length > 0 ? available : products;

    // Score all products
    const scored = pool.map(function (p) { return scoreProduct(p, answers); });

    // Sort descending by score
    scored.sort(function (a, b) { return b.score - a.score; });

    // Display percentage: amplify differences so users see meaningful spread.
    // Top product gets 97–99 based on its raw fit. Each point of raw score
    // difference from the top = 3 display-percentage-points drop.
    // This turns a typical 82-vs-78 gap into 99% vs 87% instead of 99% vs 99%.
    var topScore = scored.length > 0 ? scored[0].score : 1;
    var totalWeight = 100; // sum of all WEIGHTS
    var topFit = topScore / totalWeight; // 0–1 how well the best product fits
    var topDisplay = Math.round(Math.max(90, Math.min(99, 80 + topFit * 20)));

    scored.forEach(function (result) {
      if (topScore > 0) {
        var diff = topScore - result.score;
        result.displayPercentage = Math.max(60, Math.round(topDisplay - diff * 3));
      } else {
        result.displayPercentage = 60;
      }
    });

    return scored.slice(0, limit);
  }

  // ── Badge Helper ───────────────────────────────────────────────────────────

  function getBadge(percentage, locale) {
    const isAr = locale === 'ar';
    if (percentage >= 90) return { label: isAr ? 'أفضل اختيار' : 'Top Match', color: '#f0c417', tier: 'top' };
    if (percentage >= 80) return { label: isAr ? 'اختيار ممتاز' : 'Great Fit', color: 'rgba(var(--text-color-rgb), 0.8)', tier: 'great' };
    if (percentage >= 70) return { label: isAr ? 'خيار جيد' : 'Good Option', color: 'rgba(var(--text-color-rgb), 0.5)', tier: 'good' };
    return { label: isAr ? 'يستحق النظر' : 'Worth a Look', color: 'rgba(var(--text-color-rgb), 0.3)', tier: 'look' };
  }

  // ── Explanation Generator ──────────────────────────────────────────────────

  const STYLE_NAMES = {
    en: { Power: 'power', Control: 'control', Hybrid: 'versatile' },
    ar: { Power: 'القوة', Control: 'التحكم', Hybrid: 'التنوع' }
  };

  const SHAPE_NAMES = {
    en: { Diamond: 'diamond', Teardrop: 'teardrop', Round: 'round', Hybrid: 'hybrid' },
    ar: { Diamond: 'الماسي', Teardrop: 'الدمعة', Round: 'الدائري', Hybrid: 'الهجين' }
  };

  const EXPLANATION_TEMPLATES = {
    en: {
      style: {
        strong: 'Built for your {style} playing style.',
        moderate: 'Leans toward {style}, complementing how you play.'
      },
      level: {
        strong: 'Designed for players at your level.',
        moderate: 'A great step for someone at your experience.'
      },
      shape: {
        strong: 'Its {shape} shape directly addresses what you want to improve.',
        moderate: 'The {shape} shape offers a good balance for your goals.'
      },
      budget: {
        strong: 'Fits right within your budget.',
        moderate: 'Slightly outside your range, but worth it for the quality.'
      },
      position: {
        strong: 'Perfectly suited for your court position and playing side.',
        moderate: 'Works well with how you position yourself on court.'
      },
      weakness: {
        strong: 'Directly addresses your biggest on-court struggle.',
        moderate: 'Helps compensate for the area you want to strengthen.'
      },
      physique: {
        strong: 'Matched to your physical build for optimal handling.',
        moderate: 'A comfortable weight and balance for your frame.'
      },
      comfort: {
        strong: 'Designed with arm comfort as a priority.',
        moderate: 'Good comfort level for extended play sessions.'
      },
      fresh: 'Plus, it\'s a {year} model with the latest technology.'
    },
    ar: {
      style: {
        strong: 'متصمم لأسلوب {style} بتاعك.',
        moderate: 'بيميل لـ{style}، بيناسب طريقة لعبك.'
      },
      level: {
        strong: 'متصمم للاعبين في مستواك.',
        moderate: 'خطوة ممتازة لحد في مستواك.'
      },
      shape: {
        strong: 'شكله {shape} بيخدم اللي عايز تحسّنه.',
        moderate: 'الشكل {shape} بيدّيك توازن كويس لأهدافك.'
      },
      budget: {
        strong: 'في حدود ميزانيتك.',
        moderate: 'أغلى شوية، بس يستاهل للجودة.'
      },
      position: {
        strong: 'مناسب جداً لمركزك في الملعب وطريقة لعبك.',
        moderate: 'بيشتغل كويس مع طريقة تمركزك في الكورت.'
      },
      weakness: {
        strong: 'بيعالج أكبر نقطة ضعف عندك في الملعب.',
        moderate: 'بيساعدك تحسّن المنطقة اللي عايز تقوّيها.'
      },
      physique: {
        strong: 'مناسب لبنيتك الجسمية وبيدّيك تحكم مريح.',
        moderate: 'وزنه وتوازنه كويسين لجسمك.'
      },
      comfort: {
        strong: 'متصمم عشان يريّح إيدك ويقلل الإجهاد.',
        moderate: 'مريح في اللعب لفترات طويلة.'
      },
      fresh: 'وكمان موديل {year} بأحدث التكنولوجيا.'
    }
  };

  function generateExplanation(result, answers, locale) {
    const lang = locale === 'ar' ? 'ar' : 'en';
    const templates = EXPLANATION_TEMPLATES[lang];
    const b = result.breakdown;
    const product = result.product;

    // Rank factors by how well they scored relative to their max
    const factors = [
      { key: 'style', score: b.style, max: WEIGHTS.style },
      { key: 'level', score: b.level, max: WEIGHTS.level },
      { key: 'shape', score: b.shape, max: WEIGHTS.shape },
      { key: 'budget', score: b.budget, max: WEIGHTS.budget },
      { key: 'position', score: b.position, max: WEIGHTS.position },
      { key: 'weakness', score: b.weakness, max: WEIGHTS.weakness },
      { key: 'physique', score: b.physique, max: WEIGHTS.physique },
      { key: 'comfort', score: b.comfort, max: WEIGHTS.comfort }
    ];
    factors.sort(function (a, b) { return (b.score / b.max) - (a.score / a.max); });

    var reasons = [];
    var topTwo = factors.slice(0, 2);

    topTwo.forEach(function (f) {
      var ratio = f.score / f.max;
      var tpl = templates[f.key];
      if (!tpl) return;

      var text = ratio >= 0.8 ? tpl.strong : (ratio >= 0.5 ? tpl.moderate : null);
      if (!text) return;

      // Replace placeholders
      if (f.key === 'style') {
        var styleName = (STYLE_NAMES[lang] || {})[product.style] || product.style || '';
        text = text.replace('{style}', styleName);
      } else if (f.key === 'shape') {
        var shapeName = (SHAPE_NAMES[lang] || {})[product.shape] || product.shape || '';
        text = text.replace('{shape}', shapeName);
      }

      reasons.push(text);
    });

    // Freshness mention
    var pYear = parseInt(product.year) || 0;
    var currentYear = new Date().getFullYear();
    if (pYear >= currentYear - 1 && pYear > 0) {
      reasons.push(templates.fresh.replace('{year}', pYear));
    }

    return reasons.join(' ');
  }

  // ── Summary Generator ──────────────────────────────────────────────────────

  const SUMMARY = {
    en: {
      pattern: "Based on your answers, you're {level} who {style}. You're looking for {goal}, {budget}. {position}{weakness}{physique}{comfort}Here are the rackets we think you'll love:",
      level: {
        Beginner: 'a player just starting your padel journey',
        Intermediate: 'an intermediate player building your game',
        Advanced: 'an experienced player with serious skills',
        Professional: 'a competitive player at the highest level'
      },
      style: {
        power: 'loves to dominate with powerful shots',
        control: 'wins through precision and smart placement',
        hybrid: 'adapts your game to every situation'
      },
      goal: {
        more_power: 'a racket that adds more firepower to your smashes',
        sweet_spot: 'a forgiving racket with a generous sweet spot',
        more_control: 'a racket that gives you surgical precision',
        balance: 'a versatile all-rounder that doesn\'t compromise'
      },
      budget: {
        budget: 'and we\'ve focused on great value picks',
        mid: 'and we\'ve balanced quality with value',
        premium: 'and we\'ve picked from the premium range',
        elite: 'and we\'ve selected the absolute best'
      },
      position: {
        right_right: 'Playing right side with your right hand, you benefit from control-oriented rackets. ',
        right_left: 'On the left side with your right hand, you need extra power for those cross-court winners. ',
        left_left: 'Playing left side with your left hand, you benefit from control-oriented rackets. ',
        left_right: 'On the right side with your left hand, you need extra power for those cross-court winners. '
      },
      weakness: {
        glass_too_much: 'We\'ve prioritized rackets that help with ball control and placement. ',
        weak_smash: 'We\'ve picked rackets that maximize your smashing power. ',
        mishits: 'We\'ve focused on rackets with a forgiving sweet spot. ',
        defense_weak: 'We\'ve chosen rackets that strengthen your defensive game. '
      },
      physique: {
        athletic: 'With your strong build, you can handle heavier, more powerful rackets. ',
        average: '',
        slim: 'We\'ve focused on lighter rackets that are easier on your frame. '
      },
      comfort: {
        no_issues: '',
        slight_discomfort: 'We\'ve leaned toward more comfortable, arm-friendly options. ',
        arm_issues: 'Arm comfort was a top priority — we picked the most forgiving, lightest rackets. '
      }
    },
    ar: {
      pattern: 'بناءً على إجاباتك، أنت {level} و{style}. بتدوّر على {goal}، {budget}. {position}{weakness}{physique}{comfort}دي الراكيتات اللي نرشّحهالك:',
      level: {
        Beginner: 'لاعب لسه بادئ رحلته في البادل',
        Intermediate: 'لاعب متوسط بيطوّر لعبه',
        Advanced: 'لاعب متقدم عنده مهارات قوية',
        Professional: 'لاعب محترف بيلعب على أعلى مستوى'
      },
      style: {
        power: 'بتحب تسيطر بضربات قوية',
        control: 'بتكسب بالدقة والتمركز الذكي',
        hybrid: 'بتكيّف لعبك حسب الموقف'
      },
      goal: {
        more_power: 'راكيت يزوّد قوة سماشاتك',
        sweet_spot: 'راكيت متسامح بسويت سبوت كبيرة',
        more_control: 'راكيت يدّيك تحكم دقيق',
        balance: 'راكيت متوازن ما يضحّيش بأي حاجة'
      },
      budget: {
        budget: 'وركّزنا على أحسن قيمة',
        mid: 'ووازنّا بين الجودة والسعر',
        premium: 'واخترنا من الفئة المميزة',
        elite: 'واخترنا الأفضل بغض النظر عن السعر'
      },
      position: {
        right_right: 'بتلعب يمين بإيدك اليمين — الراكيتات اللي بتحكم أحسن هتفيدك. ',
        right_left: 'بتلعب شمال بإيدك اليمين — محتاج قوة زيادة للضربات العابرة. ',
        left_left: 'بتلعب شمال بإيدك الشمال — الراكيتات اللي بتحكم أحسن هتفيدك. ',
        left_right: 'بتلعب يمين بإيدك الشمال — محتاج قوة زيادة للضربات العابرة. '
      },
      weakness: {
        glass_too_much: 'ركّزنا على راكيتات بتساعدك في التحكم بالكورة. ',
        weak_smash: 'اخترنا راكيتات بتزوّد قوة السماش عندك. ',
        mishits: 'ركّزنا على راكيتات بسويت سبوت كبيرة ومتسامحة. ',
        defense_weak: 'اخترنا راكيتات بتقوّي لعبك الدفاعي. '
      },
      physique: {
        athletic: 'بنيتك القوية بتسمحلك تتعامل مع راكيتات أتقل وأقوى. ',
        average: '',
        slim: 'ركّزنا على راكيتات أخف عشان تناسب بنيتك. '
      },
      comfort: {
        no_issues: '',
        slight_discomfort: 'ركّزنا على راكيتات مريحة وخفيفة على الإيد. ',
        arm_issues: 'راحة الإيد كانت أولوية — اخترنا أكتر الراكيتات المتسامحة والأخف. '
      }
    }
  };

  function generateSummary(answers, locale) {
    var lang = locale === 'ar' ? 'ar' : 'en';
    var s = SUMMARY[lang];

    var levelText = (s.level || {})[answers.level] || '';
    var styleText = (s.style || {})[answers.style] || '';
    var goalText = (s.goal || {})[answers.improvement] || '';
    var budgetText = (s.budget || {})[answers.budget] || '';
    var positionText = (s.position || {})[answers.position] || '';
    var weaknessText = (s.weakness || {})[answers.weakness] || '';
    var physiqueText = (s.physique || {})[answers.physique] || '';
    var comfortText = (s.comfort || {})[answers.comfort] || '';

    return s.pattern
      .replace('{level}', levelText)
      .replace('{style}', styleText)
      .replace('{goal}', goalText)
      .replace('{budget}', budgetText)
      .replace('{position}', positionText)
      .replace('{weakness}', weaknessText)
      .replace('{physique}', physiqueText)
      .replace('{comfort}', comfortText);
  }

  // ── Smart Tip Generator ────────────────────────────────────────────────────

  function generateSmartTip(answers, locale) {
    var isAr = locale === 'ar';
    var levelIdx = LEVEL_ORDER.indexOf(answers.level);
    var budgetIdx = BUDGET_ORDER.indexOf(answers.budget);

    // Advanced/Pro player with low budget
    if (levelIdx >= 2 && budgetIdx <= 1) {
      return isAr
        ? 'نصيحة: كلاعب متقدم، الراكيت الأغلى ممكن يدّيك فرق ملحوظ في الأداء. فكّر في الاستثمار لو تقدر.'
        : 'Tip: As an advanced player, investing more in your racket can make a noticeable difference. Consider stretching your budget if you can.';
    }

    // Beginner with premium/elite budget
    if (levelIdx <= 0 && budgetIdx >= 2) {
      return isAr
        ? 'نصيحة: كلاعب مبتدئ، مش لازم تشتري الأغلى. ركّز على راكيت متسامح يساعدك تتعلم الأول، وبعدين ترقّي.'
        : 'Tip: As a beginner, you don\'t need the most expensive racket. Focus on a forgiving one that helps you learn, then upgrade later.';
    }

    // Cross-side player wanting control — interesting tension
    var posNeed = POSITION_NEED[answers.position];
    if (posNeed === 'power' && answers.style === 'control') {
      return isAr
        ? 'نصيحة: أنت بتلعب في الجانب اللي محتاج قوة أكتر، بس أسلوبك تحكم. راكيت هايبرد ممكن يدّيك أحسن توازن.'
        : 'Tip: Your court position favors power, but your style is control-focused. A hybrid racket could give you the best of both worlds.';
    }

    // Slim player wanting power — tension between build and style
    if (answers.physique === 'slim' && answers.style === 'power') {
      return isAr
        ? 'نصيحة: بنيتك الخفيفة مع أسلوب القوة — ركّز على راكيتات بتدّيك قوة من غير وزن زيادة. الشكل الماسي بوزن خفيف ممكن يكون مثالي.'
        : 'Tip: With a lighter build and a power style, look for rackets that deliver power without heavy weight. A light diamond-shaped racket could be ideal.';
    }

    // Player with arm issues wanting power — important health tip
    if (answers.comfort === 'arm_issues' && answers.style === 'power') {
      return isAr
        ? 'نصيحة مهمة: عندك مشاكل في الإيد وبتحب القوة. ركّزنا على راكيتات متسامحة بتدّيك قوة من غير ما تأذي إيدك. استشير طبيب لو الألم مستمر.'
        : 'Important tip: You have arm issues but prefer power. We\'ve focused on forgiving rackets that offer power without straining your arm. Consult a doctor if pain persists.';
    }

    // Beginner with arm issues — extra care
    if (levelIdx <= 0 && answers.comfort === 'arm_issues') {
      return isAr
        ? 'نصيحة: كمبتدئ عنده مشاكل في الإيد، أهم حاجة راكيت خفيف ومتسامح. التكنيك الصح كمان بيقلل الإجهاد.'
        : 'Tip: As a beginner with arm issues, a light and forgiving racket is key. Proper technique also helps reduce strain.';
    }

    return null;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  return {
    rankProducts: rankProducts,
    getBadge: getBadge,
    generateExplanation: generateExplanation,
    generateSummary: generateSummary,
    generateSmartTip: generateSmartTip,
    // Exposed for testing
    _scoreProduct: scoreProduct,
    _WEIGHTS: WEIGHTS
  };
})();
